import {
  CANCEL_REASON_LABELS,
  CancelReason,
  ORDER_STATUS_LABELS,
  type Order,
  type OrderStatus,
  type OrderSummary,
  RETURN_REASON_LABELS,
  ReturnReason,
  formatBRL,
  isoDateInSaoPaulo,
} from '@deizi/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { AffixInput, Field } from '../../components/Form';
import { Modal, Panel } from '../../components/Overlays';
import { Alert } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import { centavosInput, parseCentavos } from '../../lib/numbers';
import { dayMonth, useInvalidateOrders } from '../../lib/orders-api';
import { useDebounced } from '../../lib/use-debounced';
import { EndPreview, Order as OrderSchema } from '@deizi/shared';

export const ORDER_BADGE: Record<OrderStatus, string> = {
  new: 'badge',
  in_production: 'badge info',
  to_pack: 'badge warn',
  shipped: 'badge solid',
  closed: 'badge',
  cancelled: 'badge',
  returned: 'badge err',
};

export function StatusBadge({ status }: { status: OrderStatus }) {
  return <span className={ORDER_BADGE[status]}>{ORDER_STATUS_LABELS[status]}</span>;
}

export function ChannelDot({ channel }: { channel: { name: string; color: string | null } }) {
  return (
    <span className="row sm" style={{ gap: 6 }}>
      <span className="dot" style={{ background: channel.color ?? 'var(--ink2)' }} />
      {channel.name}
    </span>
  );
}

/** "Prazo de envio amanhã, 08/10" / "Atrasado · prazo era 05/10" / "… · cumprido". */
export function shipText(o: Pick<OrderSummary, 'shipBy' | 'status' | 'late'>): {
  text: string;
  tone: 'err' | 'warn' | 'muted';
} {
  if (!o.shipBy) return { text: 'Prazo a combinar', tone: 'muted' };
  const today = isoDateInSaoPaulo();
  const d = new Date(`${today}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  const tomorrow = d.toISOString().slice(0, 10);
  if (o.late) return { text: `Atrasado · prazo de envio era ${dayMonth(o.shipBy)}`, tone: 'err' };
  if (o.status === 'shipped' || o.status === 'closed')
    return { text: `Prazo de envio ${dayMonth(o.shipBy)} · cumprido`, tone: 'muted' };
  if (o.shipBy === today) return { text: 'Prazo de envio hoje', tone: 'warn' };
  if (o.shipBy === tomorrow)
    return { text: `Prazo de envio amanhã, ${dayMonth(o.shipBy)}`, tone: 'warn' };
  return { text: `Prazo de envio ${dayMonth(o.shipBy)}`, tone: 'muted' };
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** "Marcar enviado": tracking code (required for posted methods). */
export function ShipDialog({
  order,
  onClose,
}: {
  order: Pick<Order, 'id' | 'number' | 'shippingMethod' | 'trackingCode'>;
  onClose: () => void;
}) {
  const toast = useToast();
  const invalidate = useInvalidateOrders();
  const [code, setCode] = useState(order.trackingCode ?? '');
  const posted = order.shippingMethod === 'mercado_envios' || order.shippingMethod === 'correios';
  const [tried, setTried] = useState(false);
  const ship = useMutation({
    mutationFn: () =>
      api.post(`/orders/${order.id}/ship`, OrderSchema, { trackingCode: code.trim() || null }),
    onSuccess: () => {
      invalidate();
      toast(`${order.number} marcado como enviado.`);
      onClose();
    },
  });
  return (
    <Panel label={`Marcar ${order.number} como enviado`} onClose={onClose}>
      {posted ? (
        <Field
          id="ship-code"
          label="Código de rastreio"
          error={tried && !code.trim() ? 'Digite o rastreio ou cole o código da etiqueta.' : null}
          help={
            order.shippingMethod === 'mercado_envios'
              ? 'O código está na etiqueta do Mercado Envios, abaixo do código de barras.'
              : 'Vai junto na mensagem “enviado” para o cliente.'
          }
        >
          <input
            id="ship-code"
            className="input"
            placeholder="BR…"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </Field>
      ) : (
        <p className="sm muted">Entrega sem rastreio: o pedido fica como enviado.</p>
      )}
      {ship.isError ? <Alert tone="err">{errorMessage(ship.error)}</Alert> : null}
      <button
        type="button"
        className="btn p block"
        disabled={ship.isPending}
        onClick={() => {
          setTried(true);
          if (!posted || code.trim()) ship.mutate();
        }}
      >
        Marcar enviado
      </button>
      <button type="button" className="btn block" onClick={onClose}>
        Agora não
      </button>
    </Panel>
  );
}

/** "Fechar P-…?": the real profit becomes final. */
export function CloseDialog({ order, onClose }: { order: Order; onClose: () => void }) {
  const toast = useToast();
  const invalidate = useInvalidateOrders();
  const done = order.checklist.filter((c) => c.done).length;
  const close = useMutation({
    mutationFn: () => api.post(`/orders/${order.id}/close`, OrderSchema),
    onSuccess: (o) => {
      invalidate();
      toast(`Pedido fechado. Lucro final: ${formatBRL(o.profit.realProfitCentavos)}`);
      onClose();
    },
  });
  return (
    <Modal
      title={`Fechar ${order.number}?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={close.isPending}
            onClick={() => close.mutate()}
          >
            Fechar pedido
          </button>
        </>
      }
    >
      <p>
        O lucro real de <b>{formatBRL(order.profit.realProfitCentavos)}</b> (
        {(order.profit.realMarginBps / 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%)
        vira final e entra nos relatórios.
      </p>
      {done < 4 ? (
        <Alert tone="warn">
          O checklist está com {done} de 4 itens. Dá para fechar mesmo assim.
        </Alert>
      ) : null}
      {close.isError ? <Alert tone="err">{errorMessage(close.error)}</Alert> : null}
    </Modal>
  );
}

/** Cancel or return with the loss computed by the API's formula, previewed here. */
export function EndDialog({
  order,
  kind,
  onClose,
}: {
  order: Order;
  kind: 'cancel' | 'return';
  onClose: () => void;
}) {
  const toast = useToast();
  const invalidate = useInvalidateOrders();
  const reasons = kind === 'cancel' ? CancelReason.options : ReturnReason.options;
  const labels: Record<string, string> =
    kind === 'cancel' ? CANCEL_REASON_LABELS : RETURN_REASON_LABELS;
  const [reason, setReason] = useState<string>(reasons[0]!);
  const [note, setNote] = useState('');
  const [refund, setRefund] = useState(centavosInput(order.totalCentavos));
  const [back, setBack] = useState(kind === 'cancel');
  const refundC = parseCentavos(refund);
  const debounced = useDebounced(refundC, 300);
  const preview = useQuery({
    queryKey: ['orders', order.id, 'end-preview', debounced, back],
    queryFn: () =>
      api.post(`/orders/${order.id}/end-preview`, EndPreview, {
        refundCentavos: debounced,
        backToStock: back,
      }),
    enabled: debounced !== null,
    placeholderData: (prev) => prev,
  });
  const n = preview.data;
  const save = useMutation({
    mutationFn: () =>
      api.post(`/orders/${order.id}/${kind}`, OrderSchema, {
        reason,
        note: note.trim() || null,
        refundCentavos: refundC,
        backToStock: back,
      }),
    onSuccess: () => {
      invalidate();
      toast(
        `${kind === 'cancel' ? 'Pedido cancelado' : 'Pedido devolvido'}. Perda registrada nos relatórios.`,
      );
      onClose();
    },
  });
  return (
    <Modal
      title={
        kind === 'cancel' ? `Cancelar ${order.number}?` : `Registrar devolução de ${order.number}`
      }
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn d"
            disabled={refundC === null || save.isPending}
            onClick={() => save.mutate()}
          >
            {kind === 'cancel' ? 'Cancelar pedido' : 'Registrar devolução'}
          </button>
        </>
      }
    >
      <span className="lbl">Motivo</span>
      <div className="row wrap" style={{ gap: 6 }} role="radiogroup" aria-label="Motivo">
        {reasons.map((r) => (
          <button
            key={r}
            type="button"
            role="radio"
            aria-checked={reason === r}
            className={reason === r ? 'chip on' : 'chip'}
            onClick={() => setReason(r)}
          >
            {labels[r]}
          </button>
        ))}
      </div>
      <Field id="end-note" label="Detalhes (opcional)">
        <textarea
          id="end-note"
          className="input"
          rows={2}
          placeholder="Ex.: chegou com 3 chaveiros quebrados"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
      <Field
        id="end-refund"
        label="Valor reembolsado"
        help={`recebido: ${formatBRL(order.totalCentavos)}`}
      >
        <AffixInput
          id="end-refund"
          prefix="R$"
          value={refund}
          onChange={setRefund}
          invalid={refundC === null}
        />
      </Field>
      <button
        type="button"
        className="check sm"
        role="switch"
        aria-checked={back}
        onClick={() => setBack(!back)}
      >
        <span className={back ? 'toggle on' : 'toggle'} aria-hidden="true" />
        {back ? 'Sim, as peças voltam para a pronta entrega' : 'Não, peças perdidas'}
      </button>
      <div className="card sunk col" style={{ gap: 2, padding: 12, borderRadius: 12 }}>
        <div className="kv sm">
          <span className="muted">Custo já produzido</span>
          <span className="v">{n ? formatBRL(n.spentCentavos) : '—'}</span>
        </div>
        <div className="kv sm">
          <span className="muted">Recuperado no estoque</span>
          <span className="v">− {n ? formatBRL(n.recoveredCentavos) : '—'}</span>
        </div>
        <div className="kv sm">
          <span className="muted">Receita mantida</span>
          <span className="v">− {n ? formatBRL(Math.max(0, n.keptCentavos)) : '—'}</span>
        </div>
        <div className="kv sm">
          <b>Perda calculada</b>
          <b className="v" style={{ color: n?.lossCentavos ? 'var(--err)' : 'var(--ok)' }}>
            {!n ? '—' : n.lossCentavos ? formatBRL(n.lossCentavos) : 'sem perda'}
          </b>
        </div>
      </div>
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
    </Modal>
  );
}
